/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import RsaHeader from "../RsaHeader/RsaHeader";
import ButtonGroup from "../../../ButtonGroup/ButtonGroup";
import React, {useContext, useEffect} from "react";
import {ExdsGrid, ExdsLayout, ExdsText} from "@exds/core";
import AppContext from "../../../../AppContext";
import {useNavigate} from "react-router-dom";
import QRCode from 'react-qr-code';
import './ActivateToken.scss';
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function ActivateToken(props) {
  const {setActiveMobileAppSetupPage, setPageName} = props;
  const {qrCodeContext, isSmartLogonEnabledContext, loadAA, sidContext} = useContext(AppContext);
  const isSmartLogonEnabledBool = isSmartLogonEnabledContext.at(0) === "true";
  const sid = sidContext.at(0);
  const qrCodeUrl = qrCodeContext.at(0);
  const navigate = useNavigate();

  useEffect(() => {
    if(loadAA) {
      const pageName = 'Mobile App Setup | RSA Activate Token';
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdvanceAction = () => {
    if (isSmartLogonEnabledBool) {
      setActiveMobileAppSetupPage('smartLogOn');
    } else {
      navigate('/finish');
    }
  };

  const cancelModalDetails = {
    title: 'Are you sure you want to cancel Mobile App Setup?',
    modalActionButton: 'Yes, I want to cancel',
    continueButton: 'No, let\'s continue'
  };

  return (
    <ExdsGrid columnSpan={8} className='step-details soft-token-setup'>
      <RsaHeader />
      <ExdsText primitive="div" className='step-intro'>
        <ExdsText primitive="p" textStyle="bodySmallHeavier" className="step-number">
          Step 3 of 3
        </ExdsText>

        <ExdsText primitive="p" textStyle="bodyLargeHeavier" className="step-title">
          Activate with your unique token
        </ExdsText>
        <ExdsText primitive="p">
          Once you’ve downloaded and installed the RSA Authenticator app to your device, you’ll need to download a unique activation token to your phone, linking the app to our systems.
        </ExdsText>
        <ExdsText primitive="p" decoration="bold">
          The app may request access to take photos. Please enable these permissions.
        </ExdsText>
      </ExdsText>
      <ExdsLayout flexDirection="row" gap="2rem" alignItems="flex-start" className="qrcode-container">
        <ExdsLayout flexDirection="column">
          <QRCode value={qrCodeUrl} data-testid="testid-qrcode-image" className="qrcode-image"
                  alt="soft token qrcode"/>
          <ExdsText primitive="div" textStyle="bodyMediumHeavier" className="rsa-step">
            Your unique QR code
          </ExdsText>
        </ExdsLayout>
        <ExdsText className='scan-instr' primitive="div">
          <ol>
            <li>Open the <b>RSA Authenticator app.</b></li>
            <li>Tap <b>Get Started.</b></li>
            <li>Tap <b>QR Login.</b></li>
            <li>Scan the unique QR code using your device’s camera to download your activation token.</li>
          </ol>
        </ExdsText>
      </ExdsLayout>
      <ButtonGroup
        advanceButtonContent='Continue'
        advanceButtonAction={handleAdvanceAction}
        modalButtonContent='Cancel'
        modalButtonType='button'
        modalDetails={cancelModalDetails}
        modalAction={() => navigate('/finish')}
        backButtonContent='Back'
        backButtonAction={() => setActiveMobileAppSetupPage('createPin')}
      />
    </ExdsGrid>
  );
}
